// "New shot" in an episode. Straight into an episode it is one call, POST /episodes/{id}/shots, which creates the
// shot and places it in the episode's default scene. Into a named scene it creates the shot and places it
// there. Everything that knows how the backend models this lives in this file.
import type { Api } from "./api.ts";
import { episodeCode } from "./items.ts";
import { nextShotCode } from "./scenes.ts";
import type { Shot } from "./types.ts";
import type { ProjectData } from "./useProject.ts";

// Keep successful creation across a failed named-scene placement; retry must not create the code again.
const pending = new WeakMap<Api, Map<string, Shot>>();

/**
 * Create a shot and place it. `sceneId` puts it in a specific (named) scene instead of the episode's default
 * placement. Returns the shot; the caller reloads the project data.
 */
export async function createShotInEpisode(
  api: Api,
  ws: string,
  d: ProjectData,
  episodeId: string,
  sceneId?: string,
): Promise<Shot> {
  const episode = d.episodes.find((e) => e.id === episodeId);
  if (!episode) throw new Error("This episode no longer exists.");
  if (!sceneId) {
    // Straight into the episode: the server creates the shot and places it in the episode's default scene
    // (made on first use), which the studio never shows as a scene.
    const inEp = (d.scenes.get(episodeId) ?? []).flatMap((s) => d.shots.get(s.id) ?? []);
    const code = nextShotCode(
      [...d.shotById.values()].map((s) => s.code),
      episodeCode(episode),
      new Set(inEp.map((s) => s.id)).size,
    );
    const res = await api.request<{ shot: Shot; scene_id: string }>(
      "POST",
      `/workspaces/${ws}/episodes/${episodeId}/shots`,
      { body: { shot: { code } } },
    );
    return res.shot;
  }
  const scene = (d.scenes.get(episodeId) ?? []).find((s) => s.id === sceneId);
  if (!scene) throw new Error("This scene no longer exists.");
  const inEpisode = (d.scenes.get(episodeId) ?? []).flatMap((s) => d.shots.get(s.id) ?? []);
  const code = nextShotCode(
    [...d.shotById.values()].map((s) => s.code),
    episodeCode(episode),
    new Set(inEpisode.map((s) => s.id)).size,
  );
  let placements = pending.get(api);
  if (!placements) {
    placements = new Map();
    pending.set(api, placements);
  }
  const key = `${ws}:${d.project.id}:${episodeId}:${sceneId}`;
  const shot = placements.get(key) ?? (await api.createShot(ws, { projectId: d.project.id, code }));
  placements.set(key, shot);
  // A lost placement response may already be reflected in refreshed project data.
  const sceneShots = d.shots.get(scene.id) ?? [];
  if (!sceneShots.some((placed) => placed.id === shot.id)) {
    await api.placeShot(ws, shot.id, { sceneId: scene.id, position: sceneShots.length + 1 });
  }
  placements.delete(key);
  return shot;
}
